/**
 * 写时复制与引用计数（DESIGN.md §4.3 机制 2、§4.5）。
 *
 * 撤销系统的可行性全押在这一件事上：**创建撤销点不能复制像素**。
 * 一次画笔笔画可能涉及几十块，每块 256 KB；若每个撤销点都深拷贝，
 * 100 步历史就是几个 GB。COW 让"拍快照"退化成一次 refCount++，
 * 只有真正写入被共享的块时才付出拷贝代价——而那时拷贝是必需的，不是浪费。
 *
 * 三个角色：
 * - {@link SharedPixels}：带引用计数的一块像素。多个快照可以指向同一个实例。
 * - {@link TileSnapshot}：某一时刻的只读视图。它**不拥有**像素，只增加引用。
 * - {@link WriteGuard}：写入许可。同一块同时只能存在一个，把"并发写同一块"
 *   从未定义行为变成可检测的错误。
 */
import { BYTES_PER_TILE } from './store';
import { tileKeyString, type TileKey } from './key';

/**
 * 内存账本。
 *
 * **所有分配统计必须只有这一个来源。** 早先 TileStore 自己维护一个计数器，
 * 而快照释放像素时它无从知晓——两处记账必然对不上，表现为
 * "释放了快照但内存没降"。凡是持有像素的对象共用同一本账，
 * 谁分配谁释放都自动反映，不可能分叉。
 */
export class MemoryLedger {
  #bytes = 0;

  get bytes(): number {
    return this.#bytes;
  }

  add(bytes: number): void {
    this.#bytes += bytes;
  }

  remove(bytes: number): void {
    this.#bytes -= bytes;
  }
}

/**
 * 引用计数的像素块。
 *
 * `refCount` 的语义是**有多少个持有者认为自己需要这份数据**——当前状态算一个，
 * 每个引用它的快照各算一个。归零即无人需要，内存可以释放。
 */
export class SharedPixels {
  #refCount = 1;
  #released = false;

  private constructor(
    readonly bytes: Uint8Array,
    private readonly ledger: MemoryLedger | undefined,
  ) {
    ledger?.add(BYTES_PER_TILE);
  }

  /** 用给定内容创建。会复制一份，调用方之后修改自己的缓冲不影响这里。 */
  static from(source: Uint8Array, ledger?: MemoryLedger): SharedPixels {
    if (source.length !== BYTES_PER_TILE) {
      throw new RangeError(`块数据长度必须是 ${BYTES_PER_TILE}，收到 ${source.length}。`);
    }
    const bytes = new Uint8Array(BYTES_PER_TILE);
    bytes.set(source);
    return new SharedPixels(bytes, ledger);
  }

  /** 创建一块全零（透明）像素。 */
  static zeroed(ledger?: MemoryLedger): SharedPixels {
    return new SharedPixels(new Uint8Array(BYTES_PER_TILE), ledger);
  }

  get refCount(): number {
    return this.#refCount;
  }

  get released(): boolean {
    return this.#released;
  }

  /** 是否被多于一个持有者引用——写入前据此决定要不要克隆。 */
  get shared(): boolean {
    return this.#refCount > 1;
  }

  /** 增加一个持有者。 */
  retain(): this {
    if (this.#released) throw new Error('已释放的像素块不能再被引用。');
    this.#refCount++;
    return this;
  }

  /**
   * 减少一个持有者，归零则释放。
   *
   * @returns 本次调用是否导致释放。
   */
  release(): boolean {
    if (this.#released) return false;
    this.#refCount--;
    if (this.#refCount > 0) return false;
    this.#released = true;
    this.ledger?.remove(BYTES_PER_TILE);
    return true;
  }

  /** 克隆出一份独占的副本，供 COW 写入使用。记在同一本账上。 */
  clone(): SharedPixels {
    return SharedPixels.from(this.bytes, this.ledger);
  }
}

/**
 * 某一时刻的块内容快照。
 *
 * 创建它**不复制像素**，只是把涉及的块各 retain 一次。这正是撤销点的成本模型：
 * 与涉及的块数成正比、与像素量无关。
 *
 * 快照是只读的。它引用的像素永远不会被后续写入改变——写入方遇到共享块会先克隆，
 * 快照看到的仍是拍摄那一刻的内容。
 */
export class TileSnapshot {
  #released = false;

  constructor(private readonly entries: Map<string, SharedPixels | null>) {}

  get size(): number {
    return this.entries.size;
  }

  get released(): boolean {
    return this.#released;
  }

  /** 快照里某块的内容。`null` 表示拍摄时该块是全透明的。 */
  get(key: TileKey): Uint8Array | null {
    this.#assertAlive();
    return this.entries.get(tileKeyString(key))?.bytes ?? null;
  }

  has(key: TileKey): boolean {
    this.#assertAlive();
    return this.entries.has(tileKeyString(key));
  }

  /** 快照实际占用的字节数——只算它**独占**的部分。 */
  exclusiveBytes(): number {
    this.#assertAlive();
    let bytes = 0;
    for (const pixels of this.entries.values()) {
      if (pixels !== null && pixels.refCount === 1) bytes += BYTES_PER_TILE;
    }
    return bytes;
  }

  /** 键的迭代，供历史系统计算差分。 */
  *keys(): Generator<string> {
    this.#assertAlive();
    yield* this.entries.keys();
  }

  /**
   * 释放快照。被它独占的块随之释放内存。
   *
   * 幂等：重复释放不会把 refCount 减成负数（那会让别人的数据提前消失）。
   */
  release(): void {
    if (this.#released) return;
    this.#released = true;
    for (const pixels of this.entries.values()) pixels?.release();
    this.entries.clear();
  }

  #assertAlive(): void {
    if (this.#released) throw new Error('快照已释放，不能再访问。');
  }
}

/**
 * 写入许可。
 *
 * **"并发写同一块"的定义**：TileStore 在单线程内使用，写入本身是串行的，
 * 但"两处代码各自持有同一块并交替写入"是真实会发生的（比如画笔正在描边时
 * 滤镜预览也想改同一块）。这种情况下谁赢取决于调用顺序，属于未定义行为，
 * 排查起来极其痛苦。
 *
 * 所以这里把它变成**可检测的错误**：同一块同时只能存在一个未释放的 guard，
 * 第二次获取直接抛错。想串行写就先释放前一个 guard，想并行改则应各自
 * 在自己的图层上操作。
 */
export class WriteGuard {
  #released = false;

  constructor(
    readonly key: TileKey,
    /** 独占的可写像素。写完即生效，无需回写。 */
    readonly bytes: Uint8Array,
    private readonly onRelease: () => void,
  ) {}

  get released(): boolean {
    return this.#released;
  }

  release(): void {
    if (this.#released) return;
    this.#released = true;
    this.onRelease();
  }
}

/** 同一块被重复获取写入许可时抛出。 */
export class ConcurrentWriteError extends Error {
  constructor(key: TileKey) {
    super(
      `块 ${tileKeyString(key)} 已有未释放的写入许可。` +
        `同一块不能被两处代码同时写入——先释放前一个 WriteGuard，或改为在各自图层上操作。`,
    );
    this.name = 'ConcurrentWriteError';
  }
}
